<template>
  <div
    class="ic-seo-results ic-seo-results--root"
    :class="{
      'ic-seo-results--iphone': isIPhone,
      'ic-seo-results--phone': isPhone || isAddress,
      'ic-seo-results--no-results': !people.length
    }"
  >
    <div class="ic-seo-results__container">
      <div class="ic-seo-results__sections">
        <div
          :class="
            isAddress
              ? 'ic-seo-results__overview-map'
              : 'ic-seo-results__overview'
          "
        >
          <tz-search-overview v-once :title="overviewTitle">
            <tz-markdown
              class="ic-seo-results__overview-count"
              v-once
              :content="paginationCount"
            />
          </tz-search-overview>
          <div
            class="ic-seo-results__map"
            v-for="overviewMap in renderIf({
              condition: isAddress && people.length
            })"
            :key="overviewMap"
          >
            <div
              class="ic-seo-results__map-placeholder"
              :style="`background-image: url(${staticMap})`"
              v-if="!showOverviewMap"
              @click="activateMap"
            >
              <button v-text="'View on Map'" />
            </div>
            <tz-map
              v-else
              :access-token="mapboxToken"
              :location="mapboxAddress"
              :static-image="staticMap"
              :show-map="showOverviewMap"
            />
          </div>
          <p
            v-for="item in renderIf({
              condition: !isPhone && !isAddress && people.length
            })"
            :key="`overview_blurb_${item}`"
            class="ic-seo-results__overview-blurb"
            v-html="overviewBlurbHtml()"
          />
          <a
            v-for="ctaBtn in renderIf({
              condition: !isPhone && !isAddress
            })"
            :key="ctaBtn"
            :href="getSearchingLink()"
            class="ic-seo-results__cta"
            rel="nofollow noopener"
            v-text="'View Records'"
          />
        </div>
        <div
          class="ic-seo-results__main-content"
          v-for="item in renderIf({ condition: people.length })"
          :key="`seo_root__${item}`"
        >
          <div class="ic-seo-results__list">
            <ic-person
              v-for="(person, index) in peopleByLocation"
              :key="index + updateKey"
              :person="person"
              :card-number="index + 1"
            />
          </div>
          <div class="ic-seo-results__pagination">
            <p class="ic-seo-results__counter" v-text="paginationCount" />
            <tz-paginate
              v-once
              :base-url="dynamicPaginationURL"
              :max-pages="maxPages"
              :items-per-page="itemsPerPage"
              :total-items="totalPeopleItems"
              :current-page="currentPage"
              :has-trailing-slash="true"
              :show-arrow-buttons="true"
            />
          </div>
        </div>
        <div
          v-for="item in renderIf({
            condition: !isPhone && !isAddress && people.length
          })"
          :key="`sidebar_${item}`"
          class="ic-seo-results__sidebar"
        >
          <ic-sidebar
            @ic-sidebar-filter-selected="sortResults"
            @ic-sidebar-filter-open="openHeaderModal"
            :button-text="filterButton.desktop_text"
            :selected-option="selectedSort"
          >
            <div
              v-for="item in renderIf({
                condition: searchFilterCityList.length > 0
              })"
              :key="item"
              v-bind:class="{
                'tz-location-search-filter__mobile-wrap': isMobile
              }"
            >
              <label
                v-for="item in renderIf({
                  condition: isMobile && searchFilterCityList.length > 1
                })"
                :key="item"
                class="tz-location-search-filter__mobile-label"
                >Filter By City</label
              >
              <tz-location-search-filter
                v-for="item in renderIf({
                  condition: searchFilterCityList.length > 1
                })"
                :key="item"
                id="cityList"
                class="tz-location-search-filter--preselected tz-location-search-filter__underline"
                v-bind:class="{
                  'tz-location-search-filter__override': hideCityFilterVerbiage,
                  'tz-location-search-filter__padding': hideCityFilterVerbiage
                }"
                :base-url="baseUrl"
                :count-prefix="!isMobile ? '' : '('"
                :count-suffix="!isMobile ? '' : ')'"
                :is-mobile="isMobile"
                :location-list="!isMobile ? searchFilterCityList : allCityList"
                :location-prepend="searchFilterLocationPrepend"
                :preselected-location="searchFilterSelectedCity"
                :title="searchFilterTitleCity"
                :show-less-text="cms.page.filters.cta_less"
                :show-more-text="cms.page.filters.cta_more"
                :nofollow-limit="cms.page.filters.nofollow_limit_city"
                :hideShowMoreButton="searchFilterCityList.length <= 15"
              />
            </div>
            <div class="tz-location-search-filter__mobile-wrap">
              <label
                v-for="item in renderIf({ condition: isMobile })"
                :key="item"
                class="tz-location-search-filter__mobile-label"
                >Filter by State</label
              >
              <tz-location-search-filter
                id="stateList"
                class="tz-location-search-filter--preselected"
                v-bind:class="{
                  'tz-location-search-filter__override': hideStateFilterVerbiage,
                  'tz-location-search-filter__padding': hideStateFilterVerbiage,
                  'tz-location-search-filter__select-override': isMobile
                }"
                :base-url="baseUrl"
                :count-prefix="!isMobile ? '' : '('"
                :count-suffix="!isMobile ? '' : ')'"
                :is-mobile="isMobile"
                :location-list="
                  !isMobile ? searchFilterStateList : allStateList
                "
                :preselected-location="searchFilterSelectedState"
                :location-prepend="searchFilterLocationPrepend"
                :title="searchFilterTitle"
                :show-less-text="cms.page.filters.cta_less"
                :show-more-text="cms.page.filters.cta_more"
                :nofollow-limit="cms.page.filters.nofollow_limit_state"
                :removeTrailingSlash="true"
                :hideShowMoreButton="searchFilterStateList.length <= 15"
              />
            </div>
          </ic-sidebar>
          <div
            v-for="item in renderIf({ condition: !isMobile })"
            :key="item"
            class="ic-seo-results__side-widget"
          >
            <ic-sidebar
              @ic-sidebar-filter-selected="sortResults"
              :button-text="filterButton.desktop_text"
              :selected-option="selectedSort"
            />
          </div>
        </div>
        <wl-card
          v-for="item in renderIf({
            condition:
              renderHeatMap && $route.name === 'seo.root' && people.length
          })"
          :key="`seo_root__${item}`"
          :title="nameHeatmapTitle"
          class="wl-card--heatmap"
        >
          <template v-slot:subtext>
            <tz-markdown :content="nameHeatmapSubtext" />
          </template>
          <tz-name-heatmap
            :selectedStates="selectedStatesForHeatmap"
            map-id="fullNameHeatMap"
            color="#c0c1ca"
          />
        </wl-card>
      </div>
      <tz-breadcrumbs :items="breadcrumbs" :site-url="siteUrl" />
    </div>
  </div>
</template>

<script>
const NBars = require('nbars/commonjs.js');
import breadcrumbsHelper from '@/assets/js/breadcrumbsHelper';
import resultHelper from '@/assets/js/resultHelper';

import WlCard from '@/components/shared/wl-card';
import WlCardItem from '@/components/shared/wl-card-item';
import TzBreadcrumbs from '@trazi/tz-breadcrumbs/src/tz-breadcrumbs.vue';
import TzLocationSearchFilter from '@trazi/tz-location-search-filter/src/tz-location-search-filter.vue';
import TzPaginate from '@trazi/tz-paginate/src/tz-paginate.vue';
import TzSearchOverview from '@trazi/tz-search-overview/src/tz-search-overview.vue';
import TzTocLinks from '@trazi/tz-toc-list/src/tz-toc-list.vue';
import TzMarkdown from '@trazi/tz-markdown/src/tz-markdown.vue';
import TzNameHeatmap from '@trazi/tz-name-heatmap/src/tz-name-heatmap.vue';
import IcPerson from '@/components/ic-person';
import IcSidebar from '@/components/ic-sidebar';
import staticMap from '@/assets/images/map-static.webp';
import determineFlowType from '@/assets/js/shared/helpers/determineFlowType';
import formatCity from '@/assets/js/shared/helpers/formatCity';
import formatFullAddressFromParams from '@/assets/js/shared/helpers/formatFullAddressFromParams';
import formatNameCaseWithSplitterAndSpacer from '@/assets/js/shared/helpers/formatNameCaseWithSplitterAndSpacer';
import formatPhoneNumber from '@/assets/js/helpers/formatPhoneNumber';
import getMobileQuery from '@/assets/js/shared/helpers/getMobileQuery';
import getResultNames from '@/assets/js/shared/helpers/getResultNames';
import getSEOMetaData from '@/assets/js/shared/helpers/getSEOMetaData';
import getStateAbbrFromName from '@/assets/js/shared/helpers/getStateAbbrFromName';
import getStateFromAbbr from '@/assets/js/shared/helpers/getStateFromAbbr';
import isIPhone from '@/assets/js/shared/helpers/isIPhone';
import mediaQueryListener from '@/assets/js/shared/helpers/mediaQueryListener';
import parseParams from '@/assets/js/shared/helpers/parseParams';
import renderIf from '@/assets/js/shared/helpers/renderIf';
import setPeopleAges from '@/assets/js/shared/helpers/setPeopleAges';
import trimSeoResultsPeople from '@/assets/js/shared/helpers/trimSeoResultsPeople';
import unlinkPhones from '@/assets/js/shared/helpers/unlinkPhones';
import getStatesList from '@/assets/js/shared/helpers/getStatesList';

import { isSafe } from 'assets/js/validationHelper';
const pageCms = require(`@/assets/cms/pages/results.json`);
const headerCms = require(`@/assets/cms/components/header.json`);
const ctaIcon = require('@/assets/images/shared/chevron_right_blue.svg');
const mobileMediaQuery = getMobileQuery({ width: 991 });

export default {
  name: 'results',
  components: {
    IcSidebar,
    WlCard,
    WlCardItem,
    TzBreadcrumbs,
    TzLocationSearchFilter,
    TzPaginate,
    TzSearchOverview,
    TzTocLinks,
    TzMarkdown,
    IcPerson,
    TzNameHeatmap,
    TzMap: () => import('@trazi/tz-map/src/tz-map.vue')
  },
  computed: {
    isPhone() {
      return this.$route.name.includes('seo.phone');
    },
    isAddress() {
      return this.$route.name.includes('seo.address');
    },
    isCityRoute() {
      return /city/.test(this.$route.name);
    },
    isStateRoute() {
      return /state/.test(this.$route.name);
    },
    paginationCount() {
      if (!this.totalPeople) {
        return '';
      }
      const total = this.totalPeople;
      const formatTotal = total;
      const current =
        this.currentPage * this.itemsPerPage - (this.itemsPerPage - 1);
      const outOf = current + this.people.length - 1;

      return NBars.transform(this.cms.page.pagination_text, {
        current,
        outOf,
        total: formatTotal
      });
    },
    noResultsFoundText() {
      return NBars.transform(this.cms.page.no_results.text, {
        firstName: this.firstName,
        lastName: this.lastName
      });
    },
    baseUrl() {
      if (this.$route.params.phone) {
        return `/reverse-phone-lookup/${this.$route.params.phone}`;
      }
      if (this.$route.params.street) {
        return `/reverse-address-lookup/${this.$route.params.street}--${this.$route.params.city}-${this.$route.params.state}`;
      }
      const paramFirst = this.firstName.replace(/ /g, '_').toLowerCase();
      const paramLast = this.lastName.replace(/ /g, '_').toLowerCase();
      return `/people/${paramFirst}-${paramLast}`;
    },
    siteUrl() {
      return `https://information.com`;
    },
    breadcrumbs() {
      const { params } = this.$route;
      const firstName = params.firstName
        ? parseParams({ word: params.firstName })
        : '';
      const lastName = params.lastName
        ? parseParams({ word: params.lastName, splitBy: ['_'], parseBy: ' ' })
        : '';
      const pageNumber = this.$route.params.page
        ? `${this.$route.params.page}`
        : '';
      const rawState = this.$route.params.state
        ? this.$route.params.state
        : null;
      const city = this.$route.params.city
        ? formatCity({ city: this.$route.params.city.replace(/-/g, ' ') })
        : null;
      const formattedState = rawState
        ? formatNameCaseWithSplitterAndSpacer({ name: rawState }).replace(
            /-/g,
            ' '
          )
        : null;

      if (params.phone) {
        return breadcrumbsHelper.setBreadcrumb({
          text: formatPhoneNumber({ phoneNumber: params.phone }),
          path: this.baseUrl + '/',
          pageNumber: this.currentPage > 1 ? this.currentPage : null,
          page: 'phone'
        });
      }
      if (this.isAddress) {
        const formattedAddress = formatFullAddressFromParams({
          streetRaw: this.$route.params.street,
          cityRaw: this.$route.params.city,
          stateAbbrRaw: getStateAbbrFromName({
            state: this.$route.params.state
          })
        });
        return breadcrumbsHelper.setBreadcrumb({
          text: formattedAddress,
          path: this.baseUrl + '/',
          pageNumber: this.currentPage > 1 ? this.currentPage : null,
          page: 'address'
        });
      }
      return breadcrumbsHelper.setResultsCrumbs(
        firstName,
        lastName,
        this.$route.params.firstName.toLowerCase(),
        this.$route.params.lastName.toLowerCase(),
        pageNumber,
        this.indexResponse,
        rawState,
        formattedState,
        city,
        this.$route.params.city
      );
    },
    profileCopy() {
      if (this.totalPeople === 1) {
        return this.cms.page.profiles_singular;
      }
      return this.cms.page.profiles_plural;
    },
    personCopy() {
      if (this.totalPeople === 1) {
        return 'Person';
      }
      return 'People';
    },
    selectedStatesForHeatmap() {
      const statesList = getStatesList();
      const stateNames = Object.values(statesList);
      return this.searchFilterStateList
        .filter(state => stateNames.includes(state.displayName)) // Filter states not in stateNames
        .map(state => {
          return {
            displayName: state.displayName,
            urlValue: this.baseUrl + state.urlValue
          };
        });
    },
    nameHeatmapTitle() {
      const statePluraliltyText =
        this.searchFilterStateList.length === 1 ? 'State' : 'States';

      return NBars.transform(this.cms.page.name_heatmap.title, {
        firstName: this.firstName,
        lastName: this.lastName,
        stateCount: `${this.searchFilterStateList.length} ${statePluraliltyText}`
      });
    },
    nameHeatmapSubtext() {
      return NBars.transform(this.cms.page.name_heatmap.subtext, {
        firstName: this.firstName,
        lastName: this.lastName
      });
    },
    overviewTitle(opts = {}) {
      const profile = this.profileCopy;

      if (this.isPhone) {
        const person = this.personCopy;
        const title = this.cms.page.search_overview.phone_page.title;
        const params = {
          phone: this.phone,
          total: this.totalPeople,
          person
        };

        return NBars.transform(title, params);
      }
      if (this.isAddress) {
        const person = this.personCopy;
        const title = this.cms.page.search_overview.address_page.title;
        const params = {
          fullAddress: this.fullAddress,
          total: this.totalPeople,
          person
        };

        return NBars.transform(title, params);
      }
      let title = this.cms.page.search_overview.root_page.title;
      const params = {
        firstName: this.firstName,
        lastName: this.lastName,
        total: this.totalPeople,
        profile
      };
      // Change title and update params for city/state routes
      if (this.isStateRoute) {
        const stateAbbr = getStateAbbrFromName({
          state: this.$route.params.state.replace(/-|_/g, ' ')
        });
        const state = getStateFromAbbr({ stateAbbr, includeTerritories: true });
        title = this.cms.page.search_overview.state_page.title;
        params['location'] = `${state}(${stateAbbr})`;
      } else if (this.isCityRoute) {
        const stateAbbr = getStateAbbrFromName({
          state: this.$route.params.state.replace(/-|_/g, ' ')
        });
        title = this.cms.page.search_overview.city_page.title;
        params['state'] = stateAbbr;
        params['city'] = formatCity({ city: this.$route.params.city });
      }
      return NBars.transform(title, params);
    },
    peopleByLocation() {
      // order results with searched state/user location first if sorting by relevance
      const state =
        this.$route.params.state ?? this.$store.getters['location/state'];
      if (state && this.selectedSort === 'relevance') {
        return [...this.people].sort((first, second) => {
          return first.state === state ? -1 : second.state === state ? 1 : 0;
        });
      }
      return this.people;
    }
  },
  async asyncData({
    $bugsnag,
    app,
    params,
    req,
    route,
    redirect,
    error,
    $searchSDK
  }) {
    const isInvalidParam = value => {
      return !value || value === 'undefined' || value.toLowerCase() === 'null';
    };

    const city = isInvalidParam(params.city) ? undefined : params.city;
    const state = isInvalidParam(params.state) ? undefined : params.state;

    // Invalid location redirect (e.g. /texas/null → state, /null/city → root)
    if (
      (route.name === 'seo.city' || route.name === 'seo.state') &&
      (!state || (route.name === 'seo.city' && !city))
    ) {
      const redirectRouteName = state ? 'seo.state' : 'seo.root';
      const { href } = app.router.resolve({
        name: redirectRouteName,
        params: {
          firstName: params.firstName,
          lastName: params.lastName,
          ...(state ? { state } : {}),
          ...(params.page ? { page: params.page } : {})
        }
      });
      return redirect(301, href);
    }

    if (!process.server) {
      return;
    }

    const userAgent = req.headers['user-agent'];
    const street = params.street;
    const stateShort = state
      ? getStateAbbrFromName({ state: state.replace(/-|_/g, ' ') })
      : undefined;
    const cityName = city
      ? formatCity({ city: city.replace(/-|_/g, ' ') })
      : undefined;
    const isState = state && !city;
    const isCity = state && city && !params.street;
    const isPhone = params.phone && params.phone !== 'undefined';
    const isAddress = params.street && params.street !== 'undefined';
    const isPhoneorAddress = params.phone || params.street;
    const fullAddress = formatFullAddressFromParams({
      streetRaw: street,
      cityRaw: city,
      stateAbbrRaw: stateShort
    });
    // Replace # with "Unit" to make it more geocoding-friendly for mapbox
    const mapboxAddress = fullAddress
      ? fullAddress.replace(/#\s*(\d+)/g, 'Unit $1').replace(/#\s*/g, 'Unit ')
      : fullAddress;

    const firstName = isPhoneorAddress
      ? ''
      : getResultNames({ params }).firstName;
    const lastName = isPhoneorAddress
      ? ''
      : parseParams({
          word: getResultNames({ params }).lastName,
          splitBy: ['_'],
          parseBy: ' '
        });
    const firstNameRequestFormat = isPhoneorAddress
      ? ''
      : getResultNames({ params }).firstNameRequestFormat;
    const lastNameRequestFormat = isPhoneorAddress
      ? ''
      : getResultNames({ params }).lastNameRequestFormat;

    const flowType = determineFlowType({
      isState,
      isCity,
      isPhone,
      isAddress
    });

    const payloadBase = {
      itemsPerPage: 25,
      maxPages: 10,
      premiumMaxPeople: pageCms.premium_info.max,
      premiumInfoResults: null,
      totalPeople: 0,
      totalPeopleItems: 0,
      recordPlural: 'records',
      matchPlural: 'matches'
    };

    try {
      // Redirect to page 1 if page is greater than 10 - Temporary Scraping Redirect
      if (params?.page && parseInt(params?.page) > 10) {
        return redirect(301, `${route.path.replace(/\/\d+\/?$/, '')}`);
      }

      //make request
      const payload = await resultHelper.buildPayload(
        params,
        payloadBase,
        $searchSDK,
        userAgent,
        flowType,
        $bugsnag
      );
      //if no results
      if (
        !payload.people ||
        !payload.people.length ||
        !payload.totalPeopleItems ||
        payload.redirect === true
      ) {
        if (flowType === 'phone' || flowType === 'address') {
          //return empty payload (will trigger blank page with search refine modal)
          return {
            ...payloadBase,
            phone: params.phone ?? null,
            currentPage: 1,
            fullAddress: fullAddress
          };
        } else {
          //redirect to /pf/:id/searching
          let params = resultHelper.convertArrayOfObjectsToObject(
            headerCms.cp_search_params,
            'name',
            'value'
          );
          params = { ...params, firstName, lastName };

          if (state) {
            params.state = stateShort;
          }

          if (city) {
            params.city = cityName.replace(/ /g, '_');
          }

          const resultsParams = new URLSearchParams(params);
          return redirect(
            `/pf/${pageCms.landing_id}/searching/?${resultsParams}`
          );
        }
      }

      payload.firstName = firstName;
      payload.lastName = lastName;
      payload.fullAddress = fullAddress;
      payload.mapboxAddress = mapboxAddress;
      payload.locationList = payload.locationList.filter(({ name }) => name);
      const immutableLocations = payload.locationList;
      payload.locationsCountSort = immutableLocations.sort((a, b) =>
        a.count < b.count ? 1 : -1
      );

      const peopleArrayCopy =
        payload.peoplePages[payload.currentPageIndex] || [];
      const setPeopleArrayAges = setPeopleAges({ people: peopleArrayCopy });

      // Used when people sort results by relevance. Displays results from initial load
      //TODO: figure out why replacing this with standalone unlinkRelatives function from shared/helpers
      //causes names that should be linked to be string and vice versa (opposite of intended funtionality)
      payload.peopleCopy = resultHelper.unlinkRelatives(
        setPeopleArrayAges,
        firstNameRequestFormat,
        lastNameRequestFormat
      );

      if (flowType === 'city' && payload.searchFilterCityList.length) {
        // reprocess to remove link to the same page
        payload.searchFilterCityList[1].urlValue = `${payload.searchFilterCityList[1].urlValue
          .replace(/ /g, '-')
          .toLowerCase()}#`;
      }
      if (flowType === 'state') {
        if (payload.searchFilterStateList.length) {
          payload.searchFilterStateList[1].urlValue = `${payload.searchFilterStateList[1].urlValue.toLowerCase()}#`;
        }
      }

      // Filter out persons with no CID or City aka Bad Data / Incomplete Data
      payload.people = payload.people.filter(
        ({ cid, city }) => cid && city && city !== ''
      );

      // Filter out addresses containing null values from people response
      for (let i = 0; i < payload.people.length; i++) {
        const person = payload.people[i];
        person.addresses = person.addresses.filter(address => {
          const { city, state } = address;
          return (
            city &&
            city.toLowerCase() !== 'null' &&
            state &&
            state.toLowerCase() !== 'null'
          );
        });
      }

      payload.people =
        unlinkPhones({
          people: trimSeoResultsPeople(payload.people, {
            maxAliases: pageCms.person_card.count_alias,
            maxPhones: pageCms.person_card.count_phone,
            maxEmails: pageCms.person_card.count_email,
            maxRelatives: pageCms.person_card.count_relatives,
            maxLocations: pageCms.person_card.count_address,
            maxFullAddresses: pageCms.person_card.count_address_street
          }),
          phone: params.phone
        }) || [];
      payload.peopleCopy =
        unlinkPhones({
          people: trimSeoResultsPeople(payload.peopleCopy, {
            maxAliases: pageCms.person_card.count_alias,
            maxPhones: pageCms.person_card.count_phone,
            maxEmails: pageCms.person_card.count_email,
            maxRelatives: pageCms.person_card.count_relatives,
            maxLocations: pageCms.person_card.count_address,
            maxFullAddresses: pageCms.person_card.count_address_street
          }),
          phone: params.phone
        }) || [];

      const payloadKeysToDropFromHydration = [
        'currentPageIndex',
        'peoplePages',
        'peopleResponse',
        'peopleFullResponse',
        'premiumInfoResults',
        'premiumMaxPeople',
        'totalPages'
      ];

      for (
        let index = 0;
        index < payloadKeysToDropFromHydration.length;
        index += 1
      ) {
        delete payload[payloadKeysToDropFromHydration[index]];
      }

      payload.isIPhone = isIPhone(userAgent);

      return payload;
    } catch (e) {
      // On any error, redirect to a 404 page
      return error({ statusCode: 404 });
    }
  },
  data() {
    const { state, city, street } = this.$route.params;
    const isStateFlow = state && !city && !street;
    const isCityFlow = state && city && !street;
    const firstName = this.firstName ?? '';
    const lastName = this.lastName ?? '';

    return {
      updateKey: '',
      ageSeparator: pageCms.person_card.separator_age,
      socialSubHeading: '',
      selectedSort: 'relevance',
      countPrefix: resultHelper.getCountPrefix(),
      countSuffix: resultHelper.getCountSuffix(),
      firstName,
      lastName,
      city,
      isCityFlow,
      state,
      isStateFlow,
      phone: null,
      fullAddress: {},
      hideStateFilterVerbiage: this.determineFilterVerbiage(
        'state',
        state,
        isStateFlow
      ),
      hideCityFilterVerbiage: this.determineFilterVerbiage(
        'city',
        state,
        isStateFlow
      ),
      isHidden: true,
      loadingVerified: false,
      personCardCtaText: pageCms.person_card.cta_text,
      premiumInfoBaseUrl: pageCms.premium_info.base,
      premiumInfoCardIcon: pageCms.premium_info.card_icon,
      premiumInfoCtaIcon: ctaIcon,
      premiumInfoDataTypes: resultHelper.convertArrayOfObjectKeysToArrayOfValues(
        pageCms.premium_info.data_types,
        'label'
      ),
      premiumInfoSubtext: pageCms.premium_info.subtext,
      premiumInfoTitle: pageCms.premium_info.title,
      premiumInfoUrlParams: resultHelper.convertArrayOfObjectsToObject(
        this.cms.page.premium_info.params,
        'name',
        'value'
      ),
      relativesSeparator: pageCms.person_card.separator,
      searchFilterLocationPrepend: '',
      searchFilterTitle: pageCms.filters.title_state,
      searchFilterTitleMobile: pageCms.filters.title_state_mobile,
      socialMediaDesktopImage: pageCms.person_card.social_media.tooltip_image,
      searchFilterTitleCity: pageCms.filters.title_city,
      searchFilterTitleCityMobile: pageCms.filters.title_city_mobile,
      searchFilterSelectedState: null,
      searchFilterSelectedCity: null,
      searchFilterCityList: [],
      searchFilterStateList: [],
      tocDesktopOffset: 75,
      tocMobileOffset: 120,
      resultHelper,
      landingId: pageCms.landing_id,
      filterButton: pageCms.filter_button,
      isIPhone: false,
      renderHeatMap: false,
      showOverviewMap: false,
      staticMap,
      renderIf,
      locationList: [],
      locationsCountSort: [],
      people: []
    };
  },
  methods: {
    overviewBlurbHtml() {
      if (this.isPhone || this.isAddress) {
        return;
      }
      const fullName = `${this.firstName} ${this.lastName}`;
      let dataSumStr = this.getDataSumsStr();
      let newDataStr;
      if (dataSumStr) {
        dataSumStr = dataSumStr.includes('Social Media')
          ? dataSumStr.replace('Social Media', 'Social-Media')
          : dataSumStr;
        const dataSumArr = dataSumStr.split(' ');
        //remove index with period
        dataSumArr.pop();
        newDataStr = '✔ ' + dataSumArr.join(' &nbsp;&nbsp;✔ ') + ' ';
        newDataStr = newDataStr.replace('Social-Media', 'Social Media');
      }
      let locationsCount = resultHelper.otherLocationsCount(this.locationList);
      let locationsHtml = resultHelper.formattedLocations(
        this.locationsCountSort
      );

      let shouldNoFollow = false;
      if (
        this.$route.name === 'seo.root' &&
        this.totalPeople <= pageCms.filters.nofollow_limit_state
      ) {
        shouldNoFollow = true;
      } else if (
        this.$route.name === 'seo.state' &&
        this.totalPeople <= pageCms.filters.nofollow_limit_city
      ) {
        shouldNoFollow = true;
      }

      if (this.$route.name === 'seo.root' || this.$route.name === 'seo.state') {
        const locationsArr = locationsHtml
          .split(' and ')
          .join(', ')
          .split(', ');
        const locationsHtmlArr = [];
        let path = this.$route.path;
        if (this.$route.params.page) {
          path = path.replace(this.$route.params.page + '/', '');
        }
        const possibleSlash = path[path.length - 1] === '/' ? '' : '/';
        locationsArr.forEach(location => {
          let linkTitle = '';
          switch (this.$route.name) {
            case 'seo.root':
              linkTitle = `${fullName} in ${location}`;
              break;
            case 'seo.state':
              linkTitle = `${fullName} in ${location}, ${getStateAbbrFromName({
                state: this.$route.params.state.replace(/-|_/g, ' ')
              })}`;
              break;
          }

          const href = `${path}${possibleSlash}${location
            .toLowerCase()
            .split(' ')
            .join('-')}/`;
          let linkOpeningTag = `<a class="person__link" title="${linkTitle}" href="${href}"`;
          if (shouldNoFollow) {
            linkOpeningTag += ` rel="nofollow"`;
          }
          const completeLink =
            linkOpeningTag + `><strong>${location}</strong></a>`;
          locationsHtmlArr.push(completeLink);
        });

        if (locationsHtmlArr.length === 2) {
          locationsHtml = locationsHtmlArr.join(' and ');
        } else if (locationsHtmlArr.length === 3) {
          locationsHtmlArr.splice(1, 0, ', ');
          locationsHtmlArr.splice(3, 0, locationsCount === 0 ? ' and ' : ', ');
          locationsHtml = locationsHtmlArr.join('');
        } else {
          locationsHtml = locationsHtmlArr.join(', ');
        }
      } else {
        locationsHtml = `<strong>${locationsHtml}</strong>`;
      }

      const otherLocations = !resultHelper.otherLocationsCount(
        this.locationList
      );
      const stateSingularOrPlural = locationsCount === 1 ? 'state' : 'states';
      const citySingularOrPlural = locationsCount === 1 ? 'city' : 'cities';
      const statesOrCities =
        this.$route.name === 'seo.root'
          ? stateSingularOrPlural
          : citySingularOrPlural;
      // cap additional states to 47 to exclude territories
      if (statesOrCities === 'states' && locationsCount >= 47) {
        locationsCount = 47;
      }
      const otherLocationsText = otherLocations
        ? ''
        : ` and ${locationsCount} other ${statesOrCities}`;

      return `${newDataStr}${fullName} found in ${locationsHtml}${otherLocationsText}. Find ${fullName}'s <a href="/reverse-address-lookup/" title="Reverse Address Lookup"
              ><strong>home address</strong></a
            >,
            <a href="/reverse-phone-lookup/" title="Reverse Phone Lookup"
              ><strong>phone numbers</strong></a
            >, background check, social media profiles,
            <strong>email</strong> addresses, age, birthday, white pages and
            <a href="/public-records-search/" title="Public Records Search"
              ><strong>public records</strong></a
            >.`;
    },
    handleMobileFilterScroll() {
      if (this.isPhone || this.isAddress) {
        return;
      }

      const currentPosition = window.scrollY;
      const filterBar = document.querySelector('.ic-seo-results__sidebar');
      const className = 'sticky-box-shadow';
      if (currentPosition > 124) {
        filterBar.classList.add(className);
      } else {
        filterBar.classList.remove(className);
      }
    },
    determineFilterVerbiage(where, stateAbbr, isStateFlow) {
      if (!stateAbbr) {
        return false;
      }
      switch (where) {
        case 'state': {
          return !!stateAbbr;
        }
        case 'city': {
          return !isStateFlow;
        }
        default:
          return false;
      }
    },
    getPaginationURL(flowInfo) {
      const {
        state,
        isStateFlow,
        isCityFlow,
        firstName,
        lastName,
        city,
        phone,
        street
      } = flowInfo;
      if (phone) {
        return `/reverse-phone-lookup/${phone}`;
      }
      if (street) {
        return `/reverse-address-lookup/${street}--${city}-${state}`;
      }
      if (isStateFlow) {
        return `/people/${firstName}-${lastName}/${state}`;
      }
      if (isCityFlow) {
        return `/people/${firstName}-${lastName}/${state}/${city}`;
      }
      return `/people/${firstName}-${lastName}`;
    },
    sortByAge(a, b, order) {
      if (order === 'asc') {
        return a.age - b.age;
      }
      return b.age - a.age;
    },
    sortByName(a, b, order) {
      const nameA = `${a.firstname}${a.middlename ? a.middlename : ''} ${
        a.lastname
      }`;
      const nameB = `${b.firstname}${b.middlename ? b.middlename : ''} ${
        b.lastname
      }`;
      if (order === 'asc') {
        return nameA > nameB ? 1 : -1;
      }
      return nameB > nameA ? 1 : -1;
    },
    sortResults(e) {
      const selectedOption = e.target.value;
      const peopleArrCopy = this.people.slice(0);

      switch (selectedOption) {
        case 'age-asc':
          this.people = peopleArrCopy.sort((a, b) =>
            this.sortByAge(a, b, 'asc')
          );
          break;
        case 'age-desc':
          this.people = peopleArrCopy.sort((a, b) => this.sortByAge(a, b));
          break;
        case 'name-asc':
          this.people = peopleArrCopy.sort((a, b) =>
            this.sortByName(a, b, 'asc')
          );
          break;
        case 'name-desc':
          this.people = peopleArrCopy.sort((a, b) => this.sortByName(a, b));
          break;
        case 'relevance':
          this.people = this.peopleCopy;
          break;
      }
      this.updateKey = Date.now();
      this.selectedSort = e.target.value;
    },
    openHeaderModal() {
      document.body.classList.add('body--show-modal');
    },
    setIsMobile(e) {
      this.isMobile = e.matches;
    },
    getDataSumsStr() {
      if (this.isPhone || this.isAddress || this.people.length < 1) {
        return;
      }

      const peopleDataSums = this.peopleDataSums;
      const {
        address: addressCount,
        phone: phoneCount,
        email: emailCount,
        social: socialCount
      } = peopleDataSums;

      const dataSumsArr = [];
      if (addressCount) {
        dataSumsArr.push(`Address(${addressCount})`);
      }
      if (phoneCount) {
        dataSumsArr.push(`Phone(${phoneCount})`);
      }
      if (emailCount) {
        dataSumsArr.push(`Email(${emailCount})`);
      }
      if (socialCount) {
        dataSumsArr.push(`Social Media(${socialCount})`);
      }

      return dataSumsArr.join(' ') + '. ';
    },
    activateMap() {
      this.showOverviewMap = true;
    },
    getSearchingLink() {
      const stateAbbr = getStateAbbrFromName({
        state: this.$route.params.state?.replace(/-|_/g, ' ')
      });
      const landingID = this.cms.global.default_landing_id || '8170';

      let href = `${this.siteUrl}/pf/${landingID}/searching/`;
      const query = this.$route.query;
      let params = {};
      if (Object.keys(query).length === 0) {
        params = {
          firstName: this.firstName,
          lastName: this.lastName,
          state: stateAbbr || '',
          city: this.city || '',
          aid: '26'
        };
        href += `?${new URLSearchParams(params)}`;
      } else {
        if (!query.firstName) {
          params.firstName = this.firstName;
        }
        if (!query.lastName) {
          params.lastName = this.lastName;
        }
        if (!query.state && stateAbbr) {
          params.state = stateAbbr;
        }
        if (!query.city && this.city) {
          params.city = this.city;
        }
        if (!query.aid) {
          params.aid = '26';
        }
        href += `?${new URLSearchParams(params)}`;
      }

      return href;
    }
  },
  async validate({ params }) {
    return isSafe(params);
  },
  created() {
    const flowInfo = {
      state: this.state ? this.state.toLowerCase() : this.state,
      isStateFlow: this.isStateFlow,
      isCityFlow: this.isCityFlow,
      firstName: this.firstName ? this.firstName.toLowerCase() : '',
      lastName: this.lastName ? this.lastName.toLowerCase() : '',
      city: this.city ? this.city.toLowerCase() : this.city,
      phone: this.$route.params.phone || '',
      street: this.$route.params.street?.toLowerCase() || null
    };

    this.dynamicPaginationURL = this.getPaginationURL(flowInfo);
  },
  mounted() {
    if (!this.totalPeopleItems) {
      this.openHeaderModal();
    }

    mediaQueryListener({
      selector: mobileMediaQuery,
      handleMethod: this.setIsMobile
    });
    this.isMobile = mobileMediaQuery.matches;

    if (this.isMobile && (!this.isPhone || !this.isAddress)) {
      window.addEventListener('scroll', this.handleMobileFilterScroll);
    }
    this.$nextTick(() => {
      this.renderHeatMap = true;
    });

    document.querySelector('body').addEventListener(
      'click',
      function(e) {
        const anchor = e.target.closest('a');
        if (anchor !== null) {
          e.preventDefault();
          let url = anchor.href.toString();

          // Map links return an SVGAnimatedString object. We need to extract the URL using baseVal
          if (anchor.href.baseVal) {
            // For SVG anchors
            url = anchor.href.baseVal.toString();
          }

          if (url.includes('?')) {
            url += `&${new URLSearchParams(window.location.search)}`;
          } else {
            url += window.location.search;
          }

          window.location.href = url;
        }
      },
      false
    );
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleMobileFilterScroll);
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          page: pageCms,
          global: require(`@/assets/cms/global.json`)
        };
      }
    }
  },
  head() {
    const page = this.currentPage > 1 ? ` (Page ${this.currentPage})` : '';
    const total = this.totalPeople;
    let title = '';
    let transformedMeta = [];
    const pageMeta = getSEOMetaData({
      routeName: this.$route.name,
      pageCMS: this.cms.page
    });
    const stateAbbr = this.$route.params.state
      ? getStateAbbrFromName({
          state: this.$route.params.state.replace(/-/g, ' ')
        })
      : null;
    const cityName = this.$route.params.city;
    const state = stateAbbr
      ? getStateFromAbbr({ stateAbbr, includeTerritories: true })
      : null;
    const cityCount = !this.isPhone ? this.locationList.length : 0;
    const city = cityName ? cityName : null;
    const titleFormatCity = formatCity({ city });
    const profile = this.personCopy;
    const dataSumsStr = this.getDataSumsStr();

    switch (this.$route.name) {
      case 'seo.state':
        title = resultHelper.getSEOResultsStateTitleFormatted(
          this.$route.name,
          this.cms.page,
          this.firstName,
          this.lastName,
          page,
          state,
          stateAbbr,
          total,
          profile
        );
        transformedMeta = resultHelper.getSEOResultsStateMetaFormatted(
          cityCount,
          this.firstName,
          this.lastName,
          pageMeta,
          page,
          stateAbbr,
          state,
          this.$route.params.state,
          total,
          this.locationsCountSort,
          this.locationList,
          profile,
          dataSumsStr
        );
        break;
      case 'seo.city':
        title = resultHelper.getSEOResultsCityTitleFormatted(
          this.$route.name,
          this.cms.page,
          titleFormatCity,
          this.firstName,
          this.lastName,
          page,
          state,
          stateAbbr,
          total,
          profile
        );
        transformedMeta = resultHelper.getSEOResultsCityMetaFormatted(
          titleFormatCity,
          this.firstName,
          this.lastName,
          pageMeta,
          page,
          state,
          stateAbbr,
          this.$route.params.state,
          total,
          profile,
          dataSumsStr
        );
        break;
      case 'seo.phone':
        title = resultHelper.getSEOResultsPhoneTitleFormatted({
          routeName: this.$route.name,
          pageCMS: this.cms.page,
          phone: formatPhoneNumber({ phoneNumber: this.$route.params.phone }),
          page,
          total,
          profile
        });
        transformedMeta = resultHelper.getSEOResultsPhoneMetaFormatted({
          phone: formatPhoneNumber({ phoneNumber: this.$route.params.phone }),
          pageMeta,
          total,
          profile,
          dataSumsStr,
          page
        });
        if (!this.totalPeople) {
          transformedMeta.push({
            name: 'robots',
            hid: 'robots',
            content: 'noindex, nofollow, noarchive'
          });
        }
        break;
      case 'seo.address':
        const fullAddress = this.fullAddress;
        title = NBars.transform(
          resultHelper.getSEOMetaTitleText(this.$route.name, this.cms.page),
          { fullAddress, page, total, profile }
        );
        for (let i = 0; i < pageMeta.length; i++) {
          const metaCopy = Object.assign({}, pageMeta[i]);
          const { hid, content } = metaCopy;

          metaCopy.content = NBars.transform(content, {
            fullAddress,
            page,
            total,
            profile,
            baseUrl: this.baseUrl
          });

          transformedMeta.push(metaCopy);
        }
        if (!this.totalPeople) {
          transformedMeta.push({
            name: 'robots',
            hid: 'robots',
            content: 'noindex, nofollow, noarchive'
          });
        }
        break;
      default:
        title = resultHelper.getSEOResultsRootTitleFormatted(
          this.$route.name,
          this.cms.page,
          this.firstName,
          this.lastName,
          page,
          total,
          profile
        );
        transformedMeta = resultHelper.getSEOResultsRootMetaFormatted(
          this.firstName,
          this.lastName,
          this.locationList,
          pageMeta,
          page,
          total,
          this.cms.page.meta_root_alternatives,
          this.locationsCountSort,
          profile,
          dataSumsStr
        );
    }

    const canonicalLink =
      !this.isPhone && !this.isAddress
        ? [
            {
              rel: 'canonical',
              href: resultHelper.getInformationCanonicalUrl({
                firstName: this.firstName.replace(/ /g, '_'),
                lastName: this.lastName.replace(/ /g, '_'),
                page: this.currentPage,
                city,
                state: this.$route.params.state
              })
            }
          ]
        : [
            {
              rel: 'canonical',
              href: `https://information.com${this.baseUrl}/`
            }
          ];
    return {
      title: title,
      meta: transformedMeta,
      link: canonicalLink,
      script: []
    };
  }
};
</script>
